import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Ship, Search, Filter, Plus, FileText, TrendingUp, Calendar, MapPin, Package } from 'lucide-react';

function ImportsPage() {
  const { API } = useContext(AppContext);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchImports();
  }, [statusFilter]);

  const fetchImports = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/imports`, {
        params: { status: statusFilter },
        
      });
      setImports(response.data);
    } catch (error) {
      console.error('Error fetching imports:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Order Placed': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'In Transit': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Customs Clearance': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Delivered': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const filteredData = imports.filter(item =>
    item.material.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.supplier.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.containerNo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    totalOrders: imports.length,
    inTransit: imports.filter(i => i.status === 'In Transit').length,
    customsClearance: imports.filter(i => i.status === 'Customs Clearance').length,
    delivered: imports.filter(i => i.status === 'Delivered').length,
    totalValue: imports.reduce((sum, i) => sum + i.value, 0),
  };

  if (loading) {
    return <div className="text-center py-12">Loading imports data...</div>;
  }

  return (
    <div className="space-y-6" data-testid="imports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Imports</h1>
          <p className="text-gray-500 mt-1">Track international material imports and shipments</p>
        </div>
        <button className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md">
          <Plus size={18} />
          <span>New Import Order</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-2">
            <Package className="text-[#1a237e]" size={18} />
            <p className="text-sm text-gray-500">Total Orders</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
          <div className="flex items-center space-x-2">
            <Ship className="text-blue-500" size={18} />
            <p className="text-sm text-blue-600 font-medium">In Transit</p>
          </div>
          <p className="text-2xl font-bold text-blue-700">{stats.inTransit}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
          <div className="flex items-center space-x-2">
            <FileText className="text-orange-500" size={18} />
            <p className="text-sm text-orange-600 font-medium">Customs</p>
          </div>
          <p className="text-2xl font-bold text-orange-700">{stats.customsClearance}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center space-x-2">
            <TrendingUp className="text-green-500" size={18} />
            <p className="text-sm text-green-600 font-medium">Delivered</p>
          </div>
          <p className="text-2xl font-bold text-green-700">{stats.delivered}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-5 border border-purple-200">
          <p className="text-sm text-purple-600 font-medium">Total Value</p>
          <p className="text-xl font-bold text-purple-700">${(stats.totalValue / 1000).toFixed(0)}K</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2 bg-white rounded-xl p-1.5 border border-gray-200">
          {['all', 'Order Placed', 'In Transit', 'Customs Clearance', 'Delivered'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                statusFilter === status
                  ? 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {status === 'all' ? 'All' : status}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by material, supplier, or container..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
          />
        </div>
      </div>

      {/* Imports Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Order ID</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Material</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Supplier</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Origin</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Quantity</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Container</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">ETA</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Value</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredData.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 font-mono text-sm text-[#1a237e] font-medium">{item.id}</td>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{item.material}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{item.supplier}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-1.5">
                      <MapPin size={14} className="text-gray-400" />
                      <span className="text-sm text-gray-600">{item.country}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm font-semibold text-gray-900">{(item.quantity / 1000).toFixed(1)} MT</td>
                  <td className="px-4 py-4 font-mono text-xs text-gray-600">{item.containerNo}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-1.5">
                      <Calendar size={14} className="text-gray-400" />
                      <span className="text-sm text-gray-600">{item.eta}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{item.currency} {item.value.toLocaleString()}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ImportsPage;
